import { Command as AriaKitCommand, Group as AriaKitGroup } from "ariakit";
import React from "react";
import styled from "styled-components";
import { Icon } from "../../Icon";
import { useEvent } from "../../hooks/useEvent";
import { LoadingState } from "../../states/LoadingState";
import { style, useGetBoundingClientRect, useScrollIntoView } from "../../style";

type SelectedState = "loading" | "error" | "valid";

type Selected = {
  title: React.ReactNode;
  value: string;
  state: SelectedState;
  icon?: string;
};

type Props = {
  selected: Selected[];
  onRemove(value: string): void;
  disabled: boolean;
  roundedTokens: boolean;
};

type BucketTokenProps = {
  option: Selected;
  tabIndex: 0 | -1;
  rounded: boolean;
  onRemove(value: string): void;
  onClick(): void;
};

const CONTAINER_MIN_HEIGHT_TO_ENABLE_RESIZE = 150;
const CONTAINER_MIN_HEIGHT = 58;
const CONTAINER_PADDING = 8;

const BucketContainer = styled(AriaKitGroup)<{ $disabled: boolean; $resizable: boolean; $maxHeight: number }>`
  all: unset;

  width: 100%;
  max-height: ${(props) => `${props.$maxHeight}px`};
  min-height: ${(props) => (props.$resizable ? `${CONTAINER_MIN_HEIGHT_TO_ENABLE_RESIZE}px` : "auto")};
  height: ${(props) => (props.$resizable ? `${CONTAINER_MIN_HEIGHT_TO_ENABLE_RESIZE}px` : "unset")};
  resize: ${(props) => (props.$resizable ? "vertical" : "none")};
  overflow-y: scroll;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: ${style.vars.space["2"]};
  padding: ${style.vars.space["2"]};
  box-sizing: border-box;
  border: 1px solid ${style.colors.grey.dark};
  border-bottom: 0;
  border-top-left-radius: ${style.vars.borderRadius.base};
  border-top-right-radius: ${style.vars.borderRadius.base};

  &:focus-within {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: -${style.vars.whitespace.xsmall};
  }

  ${(props) => props.$disabled && { opacity: 0.3, pointerEvents: "none" }}
`;

// TODO: need to limit the length of these tokens.
const Token = styled.div<{ $rounded: boolean; $error: boolean }>`
  all: unset;

  height: 36px;
  padding: 0 ${style.vars.space["2"]};

  display: flex;
  align-items: center;
  background: ${(props) => (props.$error ? style.colors.red.lightest : style.colors.grey.light)};
  border: 1px solid ${(props) => (props.$error ? style.colors.red.dark : style.colors.grey.light)};
  border-radius: ${(props) => (props.$rounded ? "999999px" : style.vars.borderRadius.base)};
  gap: ${style.vars.space["2"]};

  &:focus-within {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
  }
`;

const TokenIconWrapper = styled.div`
  all: unset;

  flex-grow: 0;
  display: flex;
  align-items: center;
`;

const TokenTitleWrapper = styled.div<{ $error: boolean }>`
  all: unset;

  flex-grow: 1;
  color: ${(props) => (props.$error ? style.colors.red.dark : style.colors.grey.darkest)};
`;

const TokenRemoveWrapper = styled(AriaKitCommand)<{ $error: boolean }>`
  all: unset;

  pointer-events: auto;

  flex-grow: 0;
  cursor: pointer;

  align-items: center;

  /* This is necessary so that keyboard navigation will fully scroll the entire token button into view (and not just the remove icon). */
  height: 100%;

  color: ${(props) => (props.$error ? style.colors.red.dark : style.colors.grey.darkest)};
`;

type ResizableMaxHeightResult = {
  resizable: boolean;
  maxHeight: number;
};

/**
 * This hook is used to calculate the maximum height that the container can be resized to.
 * At most it can be the height of all of the tokens and minimum 150px. But we also take into account how many tokens are
 * present in the container. If the distance between the bottom of the last token and the top of the
 * container is less than what it was previously, we shrink the container height to that distance.
 * This is to prevent a bunch of whitespace at the bottom of the container when resizing.
 */
export const useResizableMaxHeight = (
  ref: React.RefObject<HTMLElement>,
  dependencyList: React.DependencyList
): ResizableMaxHeightResult => {
  const [maxHeight, setMaxHeight0] = React.useState(CONTAINER_MIN_HEIGHT_TO_ENABLE_RESIZE);
  const getBoundingClientRect = useGetBoundingClientRect();

  React.useEffect(() => {
    const container = ref.current;

    if (!container) return;

    let mounted = true;
    const setMaxHeight = (height: number) => {
      if (mounted) {
        setMaxHeight0(height);
      }
    };

    const lastChild = container.lastElementChild;

    if (!lastChild) {
      setMaxHeight(CONTAINER_MIN_HEIGHT_TO_ENABLE_RESIZE);
      return;
    }

    requestAnimationFrame(() => {
      const containerRect = getBoundingClientRect(container);
      const childRect = getBoundingClientRect(lastChild);

      /**
       * Calculate the maximum resizable height that the container could be. We want the last row of tokens
       * to hug the bottom of the container as much as possible; otherwise, you end up with a bunch of whitespace
       * after resizing it.
       */
      const maxResizableHeight = Math.max(
        container.scrollTop + childRect.bottom - containerRect.top + CONTAINER_PADDING,
        CONTAINER_MIN_HEIGHT
      );

      /**
       * If the user manually drags the container resize handle, the `height` is set directly on the element.
       * We don't allow the container to be less than 150px; however, dragging the handle further to the top will still set this
       * value to less than 150px. If we don't clamp the height, then a re-render will trigger the container to be set to
       * whatever the height was set to. It just looks incredibly stupid, trust me.
       */
      const height = parseInt(container.style.height || `${containerRect.height}`, 10);
      const clampedHeight = Math.min(Math.max(height, CONTAINER_MIN_HEIGHT_TO_ENABLE_RESIZE), maxResizableHeight);
      container.style.height = `${clampedHeight}px`;

      setMaxHeight(maxResizableHeight);
    });

    return () => {
      mounted = false;
    };
  }, dependencyList);

  return {
    resizable: maxHeight > CONTAINER_MIN_HEIGHT_TO_ENABLE_RESIZE,
    maxHeight,
  };
};

/**
 * This is extracted from `SelectedTokensBucket` for performance reasons.
 * We would only ever re-render this component if the `tabIndex` changes,
 * and because some users add hundreds of tags, it would be best to only
 * re-render a most two BucketTokens at a time.
 */
const BucketToken = React.memo(
  React.forwardRef<HTMLButtonElement, BucketTokenProps>((props, ref) => {
    const { option, onRemove, onClick, tabIndex, rounded } = props;

    const handleClick = useEvent<React.MouseEventHandler<HTMLDivElement>>((ev) => {
      ev.preventDefault();
      onClick();
    });

    const handleRemove = useEvent<React.MouseEventHandler<HTMLButtonElement>>((ev) => {
      ev.persist();

      /**
       * Prevent default because this is a button, so we wouldn't want to submit a form.
       */
      ev.preventDefault();
      onRemove(option.value);
    });

    const icon = React.useMemo(() => {
      switch (option.state) {
        case "error": {
          return (
            <TokenIconWrapper>
              <Icon icon="error" large />
            </TokenIconWrapper>
          );
        }

        case "loading": {
          return (
            <TokenIconWrapper>
              <LoadingState small />
            </TokenIconWrapper>
          );
        }

        default: {
          if (option.icon) {
            return (
              <TokenIconWrapper>
                <Icon icon={option.icon} />
              </TokenIconWrapper>
            );
          }

          return null;
        }
      }
    }, [option.icon, option.state]);

    return (
      <Token
        data-testid={`selected-tokens-bucket-token:${option.value}`}
        data-bucket-token-container
        $rounded={rounded}
        $error={option.state === "error"}
        key={option.value}
        onClick={handleClick}
      >
        {icon}
        <TokenTitleWrapper $error={option.state === "error"}>{option.title}</TokenTitleWrapper>
        <TokenRemoveWrapper
          $error={option.state === "error"}
          data-testid={`BucketToken-${option.value}-remove-button`}
          aria-label="Remove"
          ref={ref}
          tabIndex={tabIndex}
          onClick={handleRemove}
        >
          <Icon icon="close" small />
        </TokenRemoveWrapper>
      </Token>
    );
  })
);

BucketToken.displayName = "BucketToken";

export const SelectedTokensBucket: React.FC<Props> = (props) => {
  const { selected, onRemove, disabled, roundedTokens } = props;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const focusableRef = React.useRef<HTMLButtonElement>(null);
  const [focusableIndex, setFocusableIndex] = React.useState(0);
  const maxFocusableIndex = Math.min(focusableIndex, selected.length - 1);

  const handleKeyDownCapture = useEvent<React.KeyboardEventHandler<HTMLDivElement>>((ev) => {
    ev.persist();

    switch (ev.key) {
      case "ArrowRight":
        setFocusableIndex((prev) => Math.min(prev + 1, selected.length - 1));
        queueMicrotask(() => focusableRef.current?.focus());
        break;
      case "ArrowLeft":
        setFocusableIndex((prev) => Math.max(prev - 1, 0));
        queueMicrotask(() => focusableRef.current?.focus());
        break;
    }
  });

  const handleRemoveToken = useEvent((value: string) => {
    onRemove(value);
    queueMicrotask(() => focusableRef.current?.focus());
  });

  const handleClickToken = useEvent((index: number) => {
    setFocusableIndex(index);
    queueMicrotask(() => focusableRef.current?.focus());
  });

  /**
   * We want to keep track of the previous length of the `selected` array,
   * so that if the length increases, we can change the focusable index
   * to the last element. Chances are that if the user tabs to the list,
   * they are most interested in tags that we added most recently.
   * If the length of the list decreases, then an element was removed, so
   * don't change the focusable index.
   */
  const lastSelectLengthRef = React.useRef(selected.length);

  React.useEffect(() => {
    setFocusableIndex((prev) => {
      if (selected.length > lastSelectLengthRef.current) {
        return selected.length - 1;
      } else if (prev > selected.length - 1) {
        return selected.length - 1;
      } else {
        return prev;
      }
    });

    return () => {
      lastSelectLengthRef.current = selected.length;
    };
  }, [selected.length]);

  const getBoundingClientRect = useGetBoundingClientRect();
  const scrollIntoView = useScrollIntoView();

  React.useEffect(() => {
    /**
     * When we add or remove tokens from the bucket, we want to ensure that they
     * always scroll into view. This is especially important when adding tokens
     * because it's nice to scroll to the bottom to see the token you just added.
     */
    const button = focusableRef.current;
    const container = containerRef.current;

    if (!button || !container) return;

    const bucketToken = button.parentElement;

    /**
     * Guard against changes to `BucketToken` component where
     * the parent element of the button is no longer the container.
     */
    if (!bucketToken?.dataset?.bucketTokenContainer) return;

    requestAnimationFrame(() => {
      const tokenRect = getBoundingClientRect(bucketToken);
      const containerRect = getBoundingClientRect(container);

      const withinTopBounds = tokenRect.top - containerRect.top > 0;
      const withinBottomBounds = containerRect.bottom - tokenRect.bottom > 0;

      if (withinTopBounds && withinBottomBounds) {
        return;
      }

      scrollIntoView(bucketToken, { block: "center" });
    });
  }, [focusableIndex]);

  const { resizable, maxHeight } = useResizableMaxHeight(containerRef, [selected.length]);

  return (
    <BucketContainer
      ref={containerRef}
      data-testid="selected-tokens-bucket"
      onKeyDownCapture={handleKeyDownCapture}
      tabIndex={0}
      $disabled={disabled}
      $resizable={resizable}
      $maxHeight={maxHeight}
    >
      {selected.map((option, index) => {
        const focusable = index === maxFocusableIndex;

        return (
          <BucketToken
            key={option.value}
            option={option}
            onRemove={handleRemoveToken}
            onClick={() => handleClickToken(index)}
            tabIndex={focusable ? 0 : -1}
            ref={focusable ? focusableRef : undefined}
            rounded={roundedTokens}
          />
        );
      })}
    </BucketContainer>
  );
};

SelectedTokensBucket.displayName = "SelectedTokensBucket";
